import { useMemo } from 'react'

import {
  BillingAddonReferenceFragment,
  CheckSubscriptionItemsInstalledDocument,
  CheckSubscriptionItemsInstalledQueryVariables,
  GetEnabledModulesDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'

import { useCheckSubscriptionItemsInstalled } from 'modules/marketplace_modules/hooks/useChecksubscriptionItemsInstalled'
import {
  type ModuleIncludedInPlanData,
  type ModuleIncludedInPlanDataFlexible,
  ModuleStatus,
  getModuleStatus as getModuleStatusUtil,
} from 'modules/marketplace_modules/lib/getModuleStatus'

/**
 * Hook that provides module status checking functionality
 *
 * This hook centralizes all GraphQL queries needed for module status checking:
 * - Subscription items (for direct addon purchases and bundle checking)
 * - Subscription overloads (for trial/overload status)
 * - Module status map (for subscription-based installations)
 *
 * @param addonReferences - Array of billing addon references to check subscription status for
 * @param subscriptionItemsVariables - Optional variables for the subscription items query
 * @returns Object containing getModuleStatus function and loading/error states
 */
export const useModuleStatus = (
  addonReferences: BillingAddonReferenceFragment[][] = [],
  subscriptionItemsVariables: CheckSubscriptionItemsInstalledQueryVariables = {}
) => {
  const { company } = useCurrentSession()

  // Fetch subscription items to check which modules are installed
  const {
    loading: subscriptionItemsLoading,
    error: subscriptionItemsError,
    data: subscriptionItemsData,
  } = useQuery(CheckSubscriptionItemsInstalledDocument, {
    variables: subscriptionItemsVariables,
  })

  // Fetch subscription overloads for trial/overload status
  const {
    loading: subscriptionOverloadLoading,
    error: subscriptionOverloadError,
    data: subscriptionOverloadData,
  } = useQuery(GetEnabledModulesDocument, {
    variables: { companyIds: [company.id] },
  })

  // Get subscription items array
  const subscriptionItems = useMemo(
    () => subscriptionItemsData?.billing?.subscriptionItemsConnection?.nodes || [],
    [subscriptionItemsData?.billing?.subscriptionItemsConnection?.nodes]
  )

  // Get active subscription overload
  const activeSubscriptionOverload = useMemo(
    () => subscriptionOverloadData?.companies.subscriptionOverloadsConnection.nodes[0] || null,
    [subscriptionOverloadData?.companies.subscriptionOverloadsConnection.nodes]
  )

  // Use the existing hook to get module status map for subscription-based installations
  const {
    moduleStatusMap,
    loading: moduleStatusMapLoading,
    hasEnterpriseVariant,
  } = useCheckSubscriptionItemsInstalled(addonReferences, subscriptionItemsVariables)

  /**
   * Gets the complete installation status for a module
   *
   * Checks installation status in priority order:
   * 1. Subscription status (direct addon purchase) - checked first
   * 2. Bundle status (included in plan) - checked if not found in subscription
   * 3. Subscription overload status (trial/overload) - checked if not found in subscription or bundle
   *
   * @param params - Parameters for module status checking
   * @returns ModuleStatus object with isInstalled, variant, and installedAddonInternalId
   */
  const getModuleStatus = ({
    moduleName,
    billingAddonReference,
    moduleIncludedInPlan = [],
  }: {
    moduleName: string | null | undefined
    billingAddonReference: BillingAddonReferenceFragment[] | null | undefined
    moduleIncludedInPlan?: ModuleIncludedInPlanData | ModuleIncludedInPlanDataFlexible
  }): ModuleStatus => {
    return getModuleStatusUtil({
      company,
      moduleName,
      billingAddonReference,
      moduleStatusMap,
      moduleIncludedInPlan,
      subscriptionItems,
      activeSubscriptionOverload,
    })
  }

  const loading = subscriptionItemsLoading || subscriptionOverloadLoading || moduleStatusMapLoading
  const error = subscriptionItemsError || subscriptionOverloadError

  return {
    hasEnterpriseVariant,
    getModuleStatus,
    loading,
    error,
    subscriptionItems,
    activeSubscriptionOverload,
    moduleStatusMap,
  }
}
